import axios from "axios";
import { ChecklistUploadErrorMessage } from "../constants/checklist-upload-error-messages.constants";

const S3_ERROR_CODE_REGEX = /<Code>([^<]+)<\/Code>/;

const SESSION_EXPIRED_S3_CODES = new Set([
	"ExpiredToken",
	"RequestTimeTooSkewed",
	"TokenRefreshRequired",
	"InvalidToken",
]);

const SESSION_EXPIRED_TEXT_PATTERNS = [
	"expirou",
	"expired",
	"não está em andamento",
	"nao esta em andamento",
	"answer token",
	"sessão",
	"sessao",
];

function parseS3ErrorCode(data: string): string | null {
	const codeMatch = data.match(S3_ERROR_CODE_REGEX);
	return codeMatch?.[1] ?? null;
}

function includesSessionExpiredText(value: string): boolean {
	const normalized = value.toLowerCase();
	return SESSION_EXPIRED_TEXT_PATTERNS.some((pattern) =>
		normalized.includes(pattern)
	);
}

function isSessionExpiredError(
	error: import("axios").AxiosError,
	responseText: string
): boolean {
	const status = error.response?.status;

	if (status === 401) {
		return true;
	}

	if (status === 403 && includesSessionExpiredText(responseText)) {
		return true;
	}

	const s3Code = parseS3ErrorCode(responseText);
	if (s3Code && SESSION_EXPIRED_S3_CODES.has(s3Code)) {
		return true;
	}

	if (includesSessionExpiredText(responseText)) {
		return true;
	}

	return false;
}

function isTimeoutError(error: import("axios").AxiosError): boolean {
	if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
		return true;
	}

	const message = error.message.toLowerCase();
	return message.includes("timeout") || message.includes("timed out");
}

function isServerUnstableError(status: number | undefined): boolean {
	if (!status) {
		return false;
	}

	return status >= 500 && status < 600;
}

function isConnectionError(error: import("axios").AxiosError): boolean {
	if (error.code === "ERR_NETWORK") {
		return true;
	}

	if (!(error.response || isTimeoutError(error))) {
		return true;
	}

	return false;
}

function getResponseText(error: import("axios").AxiosError): string {
	const data = error.response?.data;

	if (typeof data === "string") {
		return data;
	}

	if (data && typeof data === "object") {
		const payload = data as Record<string, unknown>;
		const apiMessage = payload.message ?? payload.Message ?? payload.title;
		if (apiMessage) {
			return String(apiMessage);
		}

		return JSON.stringify(data);
	}

	return error.message;
}

function classifyAxiosUploadError(error: import("axios").AxiosError): string {
	const status = error.response?.status;
	const responseText = getResponseText(error);

	if (isSessionExpiredError(error, responseText)) {
		return ChecklistUploadErrorMessage.sessionExpired;
	}

	if (isTimeoutError(error)) {
		return ChecklistUploadErrorMessage.timeoutOrLargeFile;
	}

	if (isServerUnstableError(status)) {
		return ChecklistUploadErrorMessage.serviceUnstable;
	}

	if (isConnectionError(error)) {
		return ChecklistUploadErrorMessage.connection;
	}

	return ChecklistUploadErrorMessage.fallback;
}

/** Mensagem amigável exibida ao usuário conforme o tipo de falha no upload. */
export function getUploadUserFacingMessage(error: unknown): string {
	if (axios.isAxiosError(error)) {
		return classifyAxiosUploadError(error);
	}

	if (error instanceof Error) {
		const message = error.message.toLowerCase();

		if (includesSessionExpiredText(message)) {
			return ChecklistUploadErrorMessage.sessionExpired;
		}

		if (message.includes("timeout") || message.includes("timed out")) {
			return ChecklistUploadErrorMessage.timeoutOrLargeFile;
		}

		if (message.includes("network")) {
			return ChecklistUploadErrorMessage.connection;
		}
	}

	return ChecklistUploadErrorMessage.fallback;
}
